import React, { useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Grid, Stack } from '@mui/material'
import store from 'state/store'
import { showFormModal } from 'state/ui'
import { useGetRegradesQuery, useGetInventoryRegradeMetaQuery } from 'services/regrades'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import RegradesListGrid from 'components/generic/datagrid/RegradesListGrid'
import InventorySummaryBar from 'components/layouts/InventorySummaryBar'
import FORM_MODALS from 'utils/ui/modals'
import { parseNumberObject } from 'utils/helpers'
import { MODAL_VIEW_MODES } from 'utils/constants'

const RegradesList = () => {
  const { id } = useParams()
  const { data: regradesListData, isLoading: isListLoading } = useGetRegradesQuery({ refetchOnMountOrArgChange: true })
  const { data: metaData, isLoading: isMetaLoading } = useGetInventoryRegradeMetaQuery({})
  const isLoading = isListLoading || isMetaLoading

  const { defaultYardUm } = useSelector(state => state.settings)
  const context = useContext(ContentLayoutContext)

  useEffect(() => {
    context.setContainerTitle('Regrades')
  }, [])

  useEffect(() => {
    if (id) {
      if (id === 'new') {
        store.dispatch(
          showFormModal({
            formModalName: FORM_MODALS.NEW_REGRADE,
            formModalProps: {
              viewMode: MODAL_VIEW_MODES.NEW,
              navigateTo: '/app/inventory/regrades',
            },
          }),
        )
      } else {
        store.dispatch(
          showFormModal({
            formModalName: FORM_MODALS.NEW_REGRADE,
            formModalProps: {
              viewMode: MODAL_VIEW_MODES.VIEW,
              navigateTo: '/app/inventory/regrades',
            },
          }),
        )
      }
    }
  }, [id])

  const summaryItems = [
    {
      label: 'Total Regrades (Last 30 Days)',
      value: metaData?.totalRegradesLast30Days,
    },
    {
      label: 'Regraded Today',
      value: metaData?.regradedToday,
    },
    {
      label: 'Largest Regraded Material By Weight',
      value: metaData?.largestRegradedMaterialByWeight,
    },
    {
      label: 'Total Value Gained',
      value: metaData?.totalValueGained?.amount
        ? parseNumberObject(metaData?.totalValueGained, { format: 'value' })
        : '',
    },
  ]

  const transformedRows = (regradesListData?.Items ?? [])
    .slice()
    .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))
    .map(regrade => ({
      id: regrade?.id,
      displayId: Number(regrade?.regradeNumber),
      updatedAt: regrade?.updatedAt ? new Date(regrade.updatedAt) : null,
      effectiveDate: regrade?.effectiveDate ? new Date(regrade.effectiveDate) : null,
      fromMaterial: regrade?.fromMaterial?.materialType?.commonName || '',
      fromQuantity: `${regrade?.fromMaterial?.weight || 0} ${defaultYardUm}`,
      toMaterials: regrade?.toMaterials || [],
      toMaterialsText:
        regrade?.toMaterials?.length > 1 ? 'Mixed' : `${regrade?.toMaterials?.[0]?.materialType?.commonName || ''}`,
      toQuantity:
        regrade?.toMaterials?.length > 1 ? 'Mixed' : `${regrade?.toMaterials?.[0]?.weight || 0} ${defaultYardUm}`,
      costTransferred: regrade?.fromMaterial?.netCost || 0,
      valueTransferred: regrade?.valueTransferred || 0, // remove when enableDisplayRegradeInformation is turned on
    }))

  return (
    <Stack>
      <InventorySummaryBar items={summaryItems} />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <RegradesListGrid rows={transformedRows} loading={isLoading} />
      </Grid>
    </Stack>
  )
}

export default RegradesList
